/**
 * Which material the cave is made of at a given depth.
 *
 * The server decides this for real — `run_engine.roll_material` picks the material
 * when a rock is materialised, and the client never gets a say. This module exists
 * only so the HUD can tell the player what is *coming*, using the same curve the
 * server rolls against (`MATERIAL_CURVE`, generated from the prototype).
 *
 * Two honesty rules, because this is a prediction and not a fact:
 *
 * - **The roll has jitter.** The server nudges the index up on a 22% roll and down
 *   on a 34% roll, so an individual rock at 300 m may be softer or harder than the
 *   band says. What this module returns is the *typical* material, and the UI must
 *   word it that way — never "next: Basalt at 305m" as a promise.
 * - **Depth is not capped, material is.** Past `saturationDepthM` the curve
 *   saturates at the hardest material and `nextMaterialAfter` returns null. That is
 *   the endless-cave rule showing through: you keep descending, you just stop
 *   meeting new rock.
 *
 * Material also carries the axe gate (`reqAxe`), which is the *other* half of what
 * the player needs to know — a tier they cannot break yet is a shopping list, not a
 * milestone. See docs/GAME_DATA.md §3.
 */
import { MATERIALS, MATERIAL_CURVE } from '@/game/data/progression';
import type { Material } from '@/game/data/types';

/** The unrounded curve position at a depth. Exposed for tests and thresholds. */
export function materialBandAt(depthM: number): number {
  const { saturationDepthM, exponent } = MATERIAL_CURVE;
  const clamped = Math.min(Math.max(depthM, 0), saturationDepthM);
  return (MATERIALS.length - 1) * (clamped / saturationDepthM) ** exponent;
}

/** Index into MATERIALS of the material typical at this depth, jitter aside. */
export function materialIndexAt(depthM: number): number {
  if (depthM < MATERIAL_CURVE.floorDepthM) return 0;
  return Math.min(MATERIALS.length - 1, Math.max(0, Math.round(materialBandAt(depthM))));
}

/** The material typically found at this depth. Never null — index 0 is the floor. */
export function materialAt(depthM: number): Material {
  return MATERIALS[materialIndexAt(depthM)]!;
}

/**
 * The shallowest depth at which `MATERIALS[index]` becomes the typical material —
 * i.e. where the band first rounds to `index`. Inverts the curve rather than
 * scanning, so it stays exact instead of landing on a whole-metre approximation.
 */
export function depthOfMaterialIndex(index: number): number {
  if (index <= 0) return 0;
  const { saturationDepthM, exponent, floorDepthM } = MATERIAL_CURVE;
  const band = index - 0.5;
  const depth = saturationDepthM * (band / (MATERIALS.length - 1)) ** (1 / exponent);
  /* The floor overrides the curve below floorDepthM, so the first transition can
     never be shallower than it. */
  return Math.max(floorDepthM, Math.ceil(depth));
}

export interface NextMaterial {
  readonly material: Material;
  readonly depthM: number;
  readonly metersAway: number;
}

/**
 * The next harder material and how far off it is, or null once the curve has
 * saturated — at which point the player is in the endless stretch.
 */
export function nextMaterialAfter(depthM: number): NextMaterial | null {
  const next = materialIndexAt(depthM) + 1;
  const material = MATERIALS[next];
  if (!material) return null;

  const at = depthOfMaterialIndex(next);
  return { material, depthM: at, metersAway: Math.max(0, at - Math.floor(depthM)) };
}
